import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Button } from './ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';
import { Lock, Users, Truck, Gift, TrendingUp, LogOut, ArrowLeft, Phone, Mail } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const formatDate = (iso) => {
  if (!iso) return '—';
  const d = new Date(iso);
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) +
    ' ' + d.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' });
};

const PROGRAM_LABELS = {
  'class-a': 'Class A CDL',
  'class-b': 'Class B CDL',
  'upgrade': 'Class B to A Upgrade',
  'not-sure': 'Not Sure Yet'
};

const StatCard = ({ icon: Icon, label, value, accent, testId }) => (
  <Card className="shadow-lg border-t-4" style={{ borderTopColor: accent }} data-testid={testId}>
    <CardContent className="pt-6 flex items-center gap-4">
      <div className="w-12 h-12 rounded-lg flex items-center justify-center flex-shrink-0" style={{ backgroundColor: accent }}>
        <Icon className="w-6 h-6 text-white" />
      </div>
      <div>
        <p className="text-3xl font-bold text-[#1e3a5f]">{value}</p>
        <p className="text-sm text-gray-600">{label}</p>
      </div>
    </CardContent>
  </Card>
);

const LeadTable = ({ columns, rows, emptyText, testId }) => (
  <div className="overflow-x-auto rounded-lg border border-gray-200" data-testid={testId}>
    <table className="w-full text-left text-sm">
      <thead className="bg-[#1e3a5f] text-white">
        <tr>
          {columns.map((c) => (
            <th key={c.key} className="px-4 py-3 font-semibold whitespace-nowrap">{c.label}</th>
          ))}
        </tr>
      </thead>
      <tbody>
        {rows.length === 0 && (
          <tr><td colSpan={columns.length} className="px-4 py-8 text-center text-gray-500">{emptyText}</td></tr>
        )}
        {rows.map((row, idx) => (
          <tr key={row.id || idx} className={idx % 2 ? 'bg-gray-50' : 'bg-white'}>
            {columns.map((c) => (
              <td key={c.key} className="px-4 py-3 whitespace-nowrap max-w-xs overflow-hidden text-ellipsis">
                {c.render ? c.render(row) : (row[c.key] || '—')}
              </td>
            ))}
          </tr>
        ))}
      </tbody>
    </table>
  </div>
);

const phoneCell = (row) => (
  <a href={`tel:${row.phone}`} className="text-[#d97706] font-medium hover:underline flex items-center gap-1">
    <Phone className="w-3 h-3" />{row.phone}
  </a>
);

const emailCell = (row) => (
  <a href={`mailto:${row.email}`} className="text-[#1e3a5f] hover:underline flex items-center gap-1">
    <Mail className="w-3 h-3" />{row.email}
  </a>
);

export default function AdminDashboard() {
  const [token, setToken] = useState(() => sessionStorage.getItem('admin_token') || '');
  const [password, setPassword] = useState('');
  const [isLoggingIn, setIsLoggingIn] = useState(false);
  const [data, setData] = useState(null);
  const [isLoading, setIsLoading] = useState(false);

  const logout = useCallback(() => {
    sessionStorage.removeItem('admin_token');
    setToken('');
    setData(null);
  }, []);

  const fetchLeads = useCallback(async (authToken) => {
    setIsLoading(true);
    try {
      const response = await axios.get(`${API}/admin/leads`, {
        headers: { Authorization: `Bearer ${authToken}` }
      });
      setData(response.data);
    } catch (error) {
      if (error.response?.status === 401) {
        toast.error(error.response?.data?.detail || 'Session expired. Please log in again.');
        logout();
      } else {
        toast.error('Failed to load leads. Please try again.');
      }
    } finally {
      setIsLoading(false);
    }
  }, [logout]);

  useEffect(() => {
    if (token) fetchLeads(token);
  }, [token, fetchLeads]);

  const handleLogin = async (e) => {
    e.preventDefault();
    setIsLoggingIn(true);
    try {
      const response = await axios.post(`${API}/admin/login`, { password });
      sessionStorage.setItem('admin_token', response.data.token);
      setToken(response.data.token);
      setPassword('');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Login failed. Please try again.');
    } finally {
      setIsLoggingIn(false);
    }
  };

  if (!token) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-[#1e3a5f] to-[#2d4a6b] flex items-center justify-center px-4">
        <Card className="w-full max-w-md shadow-2xl">
          <CardHeader className="text-center">
            <div className="w-14 h-14 bg-[#d97706] rounded-full flex items-center justify-center mx-auto mb-2">
              <Lock className="w-7 h-7 text-white" />
            </div>
            <CardTitle className="text-2xl text-[#1e3a5f]">Admin Dashboard</CardTitle>
            <p className="text-gray-500 text-sm">Diamond Trucking School — Leads</p>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleLogin} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="admin-password">Admin Password</Label>
                <Input
                  id="admin-password"
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  autoFocus
                  data-testid="admin-password-input"
                  className="border-gray-300 focus:border-[#d97706] focus:ring-[#d97706]"
                />
              </div>
              <Button
                type="submit"
                disabled={isLoggingIn}
                data-testid="admin-login-btn"
                className="w-full bg-[#d97706] hover:bg-[#b45309] text-white py-6 text-lg font-semibold"
              >
                {isLoggingIn ? 'Verifying...' : 'Unlock Dashboard'}
              </Button>
            </form>
            <Link to="/" className="block text-center text-sm text-gray-500 hover:text-[#d97706] mt-4" data-testid="admin-back-home-link">
              ← Back to website
            </Link>
          </CardContent>
        </Card>
      </div>
    );
  }

  const stats = data?.stats || {};

  return (
    <div className="min-h-screen bg-[#f0f9ff]">
      <header className="bg-[#1e3a5f] text-white shadow-md">
        <div className="container mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <img
              src="https://customer-assets.emergentagent.com/job_pro-trucker-learn/artifacts/g2xcv5lg_Diamond-Trucking-Logo.jpg"
              alt="Diamond Trucking School"
              className="h-10 w-10 object-contain rounded"
            />
            <h1 className="text-xl font-bold">Leads Dashboard</h1>
          </div>
          <div className="flex items-center gap-3">
            <Link to="/" className="hidden sm:flex items-center text-gray-200 hover:text-[#d97706] text-sm font-medium transition-colors" data-testid="admin-view-site-link">
              <ArrowLeft className="w-4 h-4 mr-1" />
              View Site
            </Link>
            <Button onClick={logout} variant="outline" size="sm" className="border-white/40 bg-transparent text-white hover:bg-white hover:text-[#1e3a5f]" data-testid="admin-logout-btn">
              <LogOut className="w-4 h-4 mr-1" />
              Logout
            </Button>
          </div>
        </div>
      </header>

      <main className="container mx-auto px-4 py-8" data-testid="admin-dashboard">
        {isLoading && !data ? (
          <p className="text-center text-gray-500 py-12" data-testid="admin-loading">Loading leads...</p>
        ) : (
          <>
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
              <StatCard icon={TrendingUp} label="Leads This Month" value={stats.this_month ?? 0} accent="#d97706" testId="stat-this-month" />
              <StatCard icon={Users} label="Student Leads" value={stats.total_students ?? 0} accent="#1e3a5f" testId="stat-students" />
              <StatCard icon={Truck} label="Fleet Leads" value={stats.total_fleet ?? 0} accent="#3b9fd9" testId="stat-fleet" />
              <StatCard icon={Gift} label="Giveaway Entries" value={stats.total_giveaway ?? 0} accent="#b45309" testId="stat-giveaway" />
            </div>

            <Tabs defaultValue="students">
              <TabsList className="mb-4 bg-white shadow-sm">
                <TabsTrigger value="students" data-testid="tab-students">Student Leads ({stats.total_students ?? 0})</TabsTrigger>
                <TabsTrigger value="fleet" data-testid="tab-fleet">Fleet Leads ({stats.total_fleet ?? 0})</TabsTrigger>
                <TabsTrigger value="giveaway" data-testid="tab-giveaway">Giveaway ({stats.total_giveaway ?? 0})</TabsTrigger>
              </TabsList>

              <TabsContent value="students">
                <LeadTable
                  testId="students-table"
                  emptyText="No student leads yet."
                  columns={[
                    { key: 'createdAt', label: 'Date', render: (r) => formatDate(r.createdAt) },
                    { key: 'name', label: 'Name' },
                    { key: 'phone', label: 'Phone', render: phoneCell },
                    { key: 'email', label: 'Email', render: emailCell },
                    { key: 'licenseType', label: 'Program', render: (r) => PROGRAM_LABELS[r.licenseType] || r.licenseType },
                    { key: 'message', label: 'Message' }
                  ]}
                  rows={data?.leads || []}
                />
              </TabsContent>

              <TabsContent value="fleet">
                <LeadTable
                  testId="fleet-table"
                  emptyText="No fleet leads yet."
                  columns={[
                    { key: 'createdAt', label: 'Date', render: (r) => formatDate(r.createdAt) },
                    { key: 'name', label: 'Contact' },
                    { key: 'company', label: 'Company' },
                    { key: 'phone', label: 'Phone', render: phoneCell },
                    { key: 'email', label: 'Email', render: emailCell },
                    { key: 'driversCount', label: 'Drivers' },
                    { key: 'message', label: 'Message' }
                  ]}
                  rows={data?.fleet_leads || []}
                />
              </TabsContent>

              <TabsContent value="giveaway">
                <LeadTable
                  testId="giveaway-table"
                  emptyText="No giveaway entries."
                  columns={[
                    { key: 'createdAt', label: 'Date', render: (r) => formatDate(r.createdAt) },
                    { key: 'fullName', label: 'Name' },
                    { key: 'phone', label: 'Phone', render: phoneCell },
                    { key: 'email', label: 'Email', render: emailCell },
                    { key: 'preferredStartMonth', label: 'Start Month' },
                    { key: 'whyCDL', label: 'Why CDL' }
                  ]}
                  rows={data?.giveaway_entries || []}
                />
              </TabsContent>
            </Tabs>
          </>
        )}
      </main>
    </div>
  );
}
